import React, { useState, useEffect } from 'react';
import { Card, Button, Select, message, Typography, Checkbox, List, Image, Pagination, Input, Spin, Progress, Modal, Tag } from 'antd';
import { ArrowLeftOutlined, SwapOutlined, CheckCircleOutlined, LoadingOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../../hooks/useTranslation';
import './index.css';

const { Option } = Select;
const { Title, Paragraph } = Typography;
const { Search } = Input;

interface UploadRecord {
  id: number;
  fileName: string;
  url: string;
  rawUrl: string;
  contentType: string;
  time: string;
  size: string;
  uid: string;
}

const FormatConvert: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [targetFormat, setTargetFormat] = useState<string>('jpg');
  const [converting, setConverting] = useState(false);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [records, setRecords] = useState<UploadRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [keyword, setKeyword] = useState('');
  
  // 转换进度相关状态
  const [progressModalVisible, setProgressModalVisible] = useState(false);
  const [progressInfo, setProgressInfo] = useState({
    current: 0,
    total: 0,
    successCount: 0,
    failCount: 0,
    message: '',
    currentUrl: '',
  });

  const formatOptions = [
    { value: 'jpg', label: 'JPG/JPEG' },
    { value: 'png', label: 'PNG' },
    { value: 'webp', label: 'WebP' },
    { value: 'gif', label: 'GIF' },
    { value: 'bmp', label: 'BMP' },
  ];

  // 获取用户ID
  const getUid = () => {
    return localStorage.getItem('fdd_uid') || '';
  };

  // 从文件名中提取格式（扩展名）
  const getFileFormat = (fileName: string): string => {
    if (!fileName) return 'unknown';
    const lastDot = fileName.lastIndexOf('.');
    if (lastDot === -1) return 'unknown';
    const ext = fileName.substring(lastDot + 1).toLowerCase();
    // 规范化格式名称
    if (ext === 'jpeg') return 'jpg';
    return ext;
  };

  // 加载上传历史
  const loadHistory = async (currentPage: number = page, searchKeyword: string = keyword, currentPageSize: number = pageSize) => {
    setLoading(true);
    try {
      const uid = getUid();
      const response = await fetch(
        `/img/history?page=${currentPage}&pageSize=${currentPageSize}&keyword=${encodeURIComponent(searchKeyword)}&uid=${uid}`,
        {
          method: 'GET',
          credentials: 'include',
        }
      );

      const result = await response.json();
      if (result.data) {
        // 只保留图片类型的记录
        const imageRecords = result.data.filter((record: UploadRecord) =>
          record.contentType && record.contentType.startsWith('image/')
        );
        setRecords(imageRecords);
        setTotal(result.total);
      } else {
        message.error(t('imageProcessing.tools.formatConvert.loadHistoryFail', { message: result.message || t('imageProcessing.tools.formatConvert.loadHistoryFailUnknown') }));
      }
    } catch (error) {
      message.error(t('imageProcessing.tools.formatConvert.networkError'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadHistory(1, '');
  }, []);

  // 切换选中状态
  const toggleSelect = (id: number) => {
    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter(i => i !== id));
    } else {
      setSelectedIds([...selectedIds, id]);
    }
  };

  // 全选/取消全选当前页
  const handleSelectAll = () => {
    if (selectedIds.length === records.length && 
        records.every(record => selectedIds.includes(record.id))) {
      // 取消全选当前页
      setSelectedIds(selectedIds.filter(id => !records.some(r => r.id === id)));
    } else {
      // 全选当前页
      const currentPageIds = records.map(record => record.id);
      const combinedIds = [...selectedIds, ...currentPageIds];
      // 使用 Array.from 替代展开运算符，避免 TypeScript 编译错误
      setSelectedIds(Array.from(new Set(combinedIds)));
    }
  };

  // 批量转换（使用SSE接收进度）
  const handleBatchConvert = async () => {
    if (selectedIds.length === 0) {
      message.warning(t('imageProcessing.tools.formatConvert.selectImagesFirst'));
      return;
    }

    const selectedRecords = records.filter(record => selectedIds.includes(record.id));
    const imageUrls = selectedRecords.map(record => record.url);
    const uid = getUid();

    // 显示进度弹窗
    setProgressModalVisible(true);
    setProgressInfo({
      current: 0,
      total: imageUrls.length,
      successCount: 0,
      failCount: 0,
      message: t('imageProcessing.tools.formatConvert.preparingConvert'),
      currentUrl: '',
    });
    setConverting(true);

    try {
      // 构建查询参数
      const params = new URLSearchParams({
        imageUrls: JSON.stringify(imageUrls),
        targetFormat: targetFormat,
        uid: uid,
      });

      // 使用 EventSource 接收 SSE 消息
      const eventSource = new EventSource(`/img/convert/batch?${params.toString()}`);

      // 监听不同类型的事件（自定义事件名称）
      eventSource.addEventListener('start', (event: MessageEvent) => {
        try {
          console.log('收到start事件:', event);
          if (!event.data) {
            console.warn('start事件没有data');
            return;
          }
          const data = JSON.parse(event.data);
          console.log('解析start数据:', data);
          setProgressInfo(prev => ({
            ...prev,
            total: data.total,
            message: data.message,
          }));
        } catch (e) {
          console.error('解析start事件失败:', e, '原始数据:', event.data);
        }
      });

      eventSource.addEventListener('progress', (event: MessageEvent) => {
        try {
          console.log('收到progress事件:', event);
          if (!event.data) {
            console.warn('progress事件没有data');
            return;
          }
          const data = JSON.parse(event.data);
          console.log('解析progress数据:', data);
          setProgressInfo(prev => ({
            ...prev,
            current: data.current,
            total: data.total,
            message: data.message,
            currentUrl: data.url,
          }));
        } catch (e) {
          console.error('解析progress事件失败:', e, '原始数据:', event.data);
        }
      });

      eventSource.addEventListener('success', (event: MessageEvent) => {
        try {
          console.log('收到success事件:', event);
          if (!event.data) {
            console.warn('success事件没有data');
            return;
          }
          const data = JSON.parse(event.data);
          console.log('解析success数据:', data);
          setProgressInfo(prev => ({
            ...prev,
            current: data.current,
            successCount: prev.successCount + 1,
            message: data.message,
          }));
        } catch (e) {
          console.error('解析success事件失败:', e, '原始数据:', event.data);
        }
      });

      // 注意：这里使用 'convert-error' 作为事件名，避免与 EventSource 的 onerror 冲突
      eventSource.addEventListener('convert-error', (event: MessageEvent) => {
        try {
          console.log('收到convert-error事件:', event);
          if (!event.data) {
            console.warn('convert-error事件没有data');
            return;
          }
          const data = JSON.parse(event.data);
          console.log('解析convert-error数据:', data);
          setProgressInfo(prev => ({
            ...prev,
            failCount: prev.failCount + 1,
            message: data.message,
          }));
        } catch (e) {
          console.error('解析convert-error事件失败:', e, '原始数据:', event.data);
        }
      });

      // 监听后端发送的error事件（用于权限检查、限额检查等）
      eventSource.addEventListener('error', (event: MessageEvent) => {
        try {
          console.log('收到error事件:', event);
          if (!event.data) {
            console.warn('error事件没有data');
            return;
          }
          const data = JSON.parse(event.data);
          console.log('解析error数据:', data);
          setProgressInfo(prev => ({
            ...prev,
            message: data.message || t('imageProcessing.tools.formatConvert.convertFailed'),
          }));
          // 延迟关闭弹窗，显示错误信息
          setTimeout(() => {
            eventSource.close();
            setProgressModalVisible(false);
            setConverting(false);
            message.error(data.message || t('imageProcessing.tools.formatConvert.convertFailed'));
          }, 2000);
        } catch (e) {
          console.error('解析error事件失败:', e, '原始数据:', event.data);
        }
      });

      eventSource.addEventListener('complete', (event: MessageEvent) => {
        try {
          console.log('收到complete事件:', event);
          if (!event.data) {
            console.warn('complete事件没有data');
            return;
          }
          const data = JSON.parse(event.data);
          console.log('解析complete数据:', data);
          setProgressInfo(prev => ({
            ...prev,
            successCount: data.successCount,
            failCount: data.failCount,
            message: data.message,
          }));
          
          // 延迟关闭弹窗，让用户看到完成信息
          setTimeout(() => {
            eventSource.close();
            setProgressModalVisible(false);
            setConverting(false);
            setSelectedIds([]);
            // 刷新历史记录
            loadHistory(page, keyword);
            
            if (data.failCount === 0) {
              message.success(data.message);
            } else {
              message.warning(data.message);
            }
          }, 2000);
        } catch (e) {
          console.error('解析complete事件失败:', e, '原始数据:', event.data);
        }
      });

      // 处理默认消息（没有事件名称的消息）
      eventSource.onmessage = (event) => {
        console.log('收到默认消息:', event);
        try {
          if (!event.data) {
            console.warn('默认消息没有data');
            return;
          }
          const data = JSON.parse(event.data);
          console.log('解析默认消息数据:', data);
          // 根据数据内容判断类型
          if (data.message) {
            setProgressInfo(prev => ({
              ...prev,
              message: data.message,
              currentUrl: data.url || prev.currentUrl,
            }));
          }
        } catch (e) {
          console.error('解析默认消息失败:', e, '原始数据:', event.data);
        }
      };

      // 处理连接错误（这是 EventSource 的内置错误，不是我们的自定义事件）
      eventSource.onerror = (error) => {
        console.error('SSE连接错误:', error);
        // 检查连接状态
        if (eventSource.readyState === EventSource.CLOSED) {
          console.log('SSE连接已关闭');
          // 如果连接已关闭且不是正常完成，可能是异常关闭
          if (converting) {
            eventSource.close();
            setProgressModalVisible(false);
            setConverting(false);
            message.error(t('imageProcessing.tools.formatConvert.connectionError'));
          }
        }
      };

    } catch (error: any) {
      setProgressModalVisible(false);
      setConverting(false);
      message.error(error?.message || t('imageProcessing.tools.formatConvert.batchConvertError'));
    }
  };

  // 搜索
  const handleSearch = (value: string) => {
    setKeyword(value);
    setPage(1);
    loadHistory(1, value);
  };

  // 分页变化（页码变化）
  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    loadHistory(newPage, keyword);
  };

  // 每页数量变化
  const handlePageSizeChange = (current: number, newPageSize: number) => {
    setPageSize(newPageSize);
    setPage(1); // 改变每页数量时重置到第一页
    loadHistory(1, keyword, newPageSize);
  };

  const isAllSelected = records.length > 0 && 
    records.every(record => selectedIds.includes(record.id));

  return (
    <div className="format-convert-page">
      <Card className="convert-card">
        <div className="convert-header">
          <Button
            type="text"
            size="small"
            icon={<ArrowLeftOutlined style={{ fontSize: 12 }} />}
            onClick={() => navigate('/image-processing')}
            className="back-button"
          >
            {t('imageProcessing.tools.formatConvert.backToImageProcessing')}
          </Button>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
            <Title level={5} style={{ margin: 0, fontSize: 16 }}>{t('imageProcessing.tools.formatConvert.batchConvertTitle')}</Title>
            <Paragraph type="secondary" style={{ margin: 0, fontSize: 12 }}>
              {t('imageProcessing.tools.formatConvert.batchConvertSubtitle')}
            </Paragraph>
          </div>
        </div>

        <div className="convert-content">
          <div className="convert-controls">
            <div className="control-item">
              <div style={{ marginBottom: 2, fontWeight: 500, fontSize: 12 }}>{t('imageProcessing.tools.formatConvert.selectTargetFormat')}</div>
              <Select
                size="small"
                value={targetFormat}
                onChange={setTargetFormat}
                style={{ width: '100%', maxWidth: 150 }}
              >
                {formatOptions.map((option) => (
                  <Option key={option.value} value={option.value}>
                    {option.label}
                  </Option>
                ))}
              </Select>
            </div>

            <div className="control-item">
              <div style={{ marginBottom: 2, fontWeight: 500, fontSize: 12 }}>{t('imageProcessing.tools.formatConvert.searchImages')}</div>
              <Search
                size="small"
                placeholder={t('imageProcessing.tools.formatConvert.searchPlaceholder')}
                allowClear
                onSearch={handleSearch}
                style={{ width: '100%', maxWidth: 200 }}
              />
            </div>

            <div className="control-item" style={{ marginTop: 20 }}>
              <Button
                type="primary"
                size="small"
                icon={<SwapOutlined style={{ fontSize: 12 }} />}
                onClick={handleBatchConvert}
                loading={converting}
                disabled={selectedIds.length === 0 || converting}
                className="convert-button"
              >
                {converting ? t('imageProcessing.tools.formatConvert.converting') : t('imageProcessing.tools.formatConvert.convertSelected', { count: selectedIds.length })}
              </Button>
            </div>
          </div>

          <div className="image-list-header">
            <Checkbox
              checked={isAllSelected}
              indeterminate={
                selectedIds.length > 0 && 
                selectedIds.length < records.filter(r => selectedIds.includes(r.id)).length
              }
              onChange={handleSelectAll}
            >
              <span style={{ fontSize: 12 }}>{t('imageProcessing.tools.formatConvert.selectAllCurrentPage', { count: selectedIds.length })}</span>
            </Checkbox>
          </div>

          <Spin spinning={loading}>
            <List
              grid={{ gutter: 12, xs: 2, sm: 3, md: 4, lg: 5, xl: 6 }}
              dataSource={records}
              locale={{ emptyText: t('imageProcessing.tools.formatConvert.noImages') }}
              renderItem={(record) => {
                const isSelected = selectedIds.includes(record.id);
                const fileFormat = getFileFormat(record.fileName);
                const formatColors: Record<string, string> = {
                  'jpg': '#ff4d4f',
                  'jpeg': '#ff4d4f',
                  'png': '#1890ff',
                  'gif': '#52c41a',
                  'webp': '#722ed1',
                  'bmp': '#fa8c16',
                };
                const formatColor = formatColors[fileFormat] || '#8c8c8c';
                return (
                  <List.Item>
                    <div
                      className={`image-item ${isSelected ? 'selected' : ''}`}
                      onClick={() => toggleSelect(record.id)}
                    >
                      <div className="image-checkbox">
                        <CheckCircleOutlined 
                          className={isSelected ? 'checked' : 'unchecked'} 
                        />
                      </div>
                      <div className="image-format-tag">
                        <Tag 
                          color={formatColor} 
                          style={{ 
                            margin: 0, 
                            fontWeight: 'bold',
                            fontSize: '11px',
                            padding: '2px 8px',
                            borderRadius: '4px',
                            border: `1px solid ${formatColor}`,
                            textTransform: 'uppercase'
                          }}
                        >
                          {fileFormat}
                        </Tag>
                      </div>
                      <div className="image-wrapper">
                        <Image
                          src={`${record.url}`}
                          alt={record.fileName}
                          preview={false}
                          placeholder={
                            <div style={{ 
                              display: 'flex', 
                              justifyContent: 'center', 
                              alignItems: 'center', 
                              height: '100%',
                              minHeight: '100px'
                            }}>
                              <Spin indicator={<LoadingOutlined style={{ fontSize: 18 }} spin />} />
                            </div>
                          }
                          fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMIAAADDCAYAAADQvc6UAAABRWlDQ1BJQ0MgUHJvZmlsZQAAKJFjYGASSSwoyGFhYGDIzSspCnJ3UoiIjFJgf8LAwSDCIMogwMCcmFxc4BgQ4ANUwgCjUcG3awyMIPqyLsis7PPOq3QdDFcvjV3jOD1boQVTPQrgSkktTgbSf4A4LbmgqISBgTEFyFYuLykAsTuAbJEioKOA7DkgdjqEvQHEToKwj4DVhAQ5A9k3gGyB5IxEoBmML4BsnSQk8XQkNtReEOBxcfXxUQg1Mjc0dyHgXNJBSWpFCYh2zi+oLMpMzyhRcASGUqqCZ16yno6CkYGRAQMDKMwhqj/fAIcloxgHQqxAjIHBEugw5sUIsSQpBobtQPdLciLEVJYzMPBHMDBsayhILEqEO4DxG0txmrERhM29nYGBddr//5/DGRjYNRkY/l7////39v///y4Dmn+LgeHANwDrkl1AuO+pmgAAADhlWElmTU0AKgAAAAgAAYdpAAQAAAABAAAAGgAAAAAAAqACAAQAAAABAAAAwqADAAQAAAABAAAAwwAAAAD9b/HnAAAHlklEQVR4Ae3dP3Ik1RnG4W+FgYxN"
                        />
                      </div>
                      <div className="image-name" title={record.fileName}>
                        {record.fileName}
                      </div>
                    </div>
                  </List.Item>
                );
              }}
            />
          </Spin>

          {total > 0 && (
            <div className="pagination-wrapper">
              <Pagination
                size={'small'}
                current={page}
                total={total}
                pageSize={pageSize}
                onChange={handlePageChange}
                onShowSizeChange={handlePageSizeChange}
                showSizeChanger
                showTotal={(total, range) => t('imageProcessing.tools.formatConvert.paginationTotal', { from: range[0], to: range[1], total })}
                pageSizeOptions={['12', '20', '40', '60', '100']}
              />
            </div>
          )}
        </div>
      </Card>

      {/* 转换进度弹窗 */}
      <Modal
        title={t('imageProcessing.tools.formatConvert.progressModalTitle')}
        open={progressModalVisible}
        closable={!converting}
        maskClosable={false}
        footer={null}
        width={450}
      >
        <div style={{ padding: '16px 0' }}>
          <div style={{ marginBottom: 12, textAlign: 'center' }}>
            <Progress
              type="circle"
              percent={progressInfo.total > 0 ? Math.round((progressInfo.current / progressInfo.total) * 100) : 0}
              status={converting ? 'active' : 'success'}
              format={(percent) => `${progressInfo.current}/${progressInfo.total}`}
              size={120}
            />
          </div>
          
          <div style={{ marginBottom: 6, textAlign: 'center', fontWeight: 500, fontSize: 13 }}>
            {progressInfo.message}
          </div>
          
          {progressInfo.currentUrl && (
            <div style={{ marginBottom: 6, fontSize: 11, color: '#8c8c8c', wordBreak: 'break-all' }}>
              {t('imageProcessing.tools.formatConvert.currentProcessing', { url: progressInfo.currentUrl })}
            </div>
          )}
          
          <div style={{ display: 'flex', justifyContent: 'space-around', marginTop: 16 }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 20, fontWeight: 'bold', color: '#52c41a' }}>
                {progressInfo.successCount}
              </div>
              <div style={{ fontSize: 11, color: '#8c8c8c' }}>{t('common.success')}</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 20, fontWeight: 'bold', color: '#ff4d4f' }}>
                {progressInfo.failCount}
              </div>
              <div style={{ fontSize: 11, color: '#8c8c8c' }}>{t('common.failed')}</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 20, fontWeight: 'bold', color: '#1890ff' }}>
                {progressInfo.total}
              </div>
              <div style={{ fontSize: 11, color: '#8c8c8c' }}>{t('common.total')}</div>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default FormatConvert;
